import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import './admin.css';

// Configure API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL + '/sync';

function Sidebar({ isOpen, onClose }) {
    // Initialize openDropdowns state from localStorage or use defaults
    const [openDropdowns, setOpenDropdowns] = useState(() => {
        const savedDropdowns = localStorage.getItem("sidebar_open_dropdowns");
        if (savedDropdowns) {
            try {
                return JSON.parse(savedDropdowns);
            } catch (e) {
                console.error("Error parsing saved dropdowns from localStorage", e);
            }
        }
        return {
            packages: false,
            services: false,
            faqs: false,
            clients: false,
            expenses: false,
            mpesa: true
        };
    });

    const [syncStatus, setSyncStatus] = useState({ pending: 0 });
    const token = localStorage.getItem("token");

    // Save openDropdowns to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem("sidebar_open_dropdowns", JSON.stringify(openDropdowns));
    }, [openDropdowns]);

    // Fetch sync status when component mounts
    useEffect(() => {
        const fetchSyncStatus = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/sync/status`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                });
                if (response.data && response.data.data) {
                    setSyncStatus(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching sync status:', error);
            }
        };

        fetchSyncStatus();
        const interval = setInterval(fetchSyncStatus, 30000);
        return () => clearInterval(interval);
    }, [token]);

    const toggleDropdown = (dropdown) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [dropdown]: !prev[dropdown]
        }));
    };

    // Close sidebar on link click (mobile)
    const handleLinkClick = () => {
        if (window.innerWidth <= 992 && onClose) {
            onClose();
        }
    };

    return (
        <div className={`sidebar ${isOpen ? 'open' : ''}`}>
            <h2 className="logo">PrimeDale</h2>
            <div className="scrollable-content">
                <nav>
                    <NavLink to="dashboard" onClick={handleLinkClick} className={({ isActive }) => isActive ? 'active' : ''}>
                        <i className="fas fa-chart-pie me-2"></i>
                        <span>Dashboard</span>
                    </NavLink>

                    {/* M-Pesa Transactions Dropdown */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.mpesa ? 'active' : ''}`}
                            onClick={() => toggleDropdown('mpesa')}
                        >
                            <i className="fas fa-money-bill-wave me-2"></i>
                            <span>M-Pesa Transactions</span>
                            {syncStatus.pending > 0 && (
                                <span className="mpesa-badge">
                                    {syncStatus.pending} pending
                                </span>
                            )}
                            <i className={`fas fa-chevron-${openDropdowns.mpesa ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.mpesa && (
                            <div className="dropdown-content">
                                <NavLink to="mpesa" onClick={handleLinkClick}>
                                    <i className="fas fa-chart-pie me-2"></i>
                                    <span>Dashboard</span>
                                    {syncStatus.pending > 0 && (
                                        <span className="mpesa-badge-small">
                                            {syncStatus.pending}
                                        </span>
                                    )}
                                </NavLink>
                                <NavLink to="mpesa/upload" onClick={handleLinkClick}>
                                    <i className="fas fa-upload me-2"></i>
                                    <span>Upload Transactions</span>
                                </NavLink>
                                <NavLink to="mpesa/transactions" onClick={handleLinkClick}>
                                    <i className="fas fa-list me-2"></i>
                                    <span>View Transactions</span>
                                </NavLink>
                                <NavLink to="mpesa/analytics" onClick={handleLinkClick}>
                                    <i className="fas fa-chart-bar me-2"></i>
                                    <span>Analytics</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Expense routes */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.expenses ? 'active' : ''}`}
                            onClick={() => toggleDropdown('expenses')}
                        >
                            <i className="fas fa-coins me-2"></i>
                            <span>Expense Management</span>
                            <i className={`fas fa-chevron-${openDropdowns.expenses ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.expenses && (
                            <div className="dropdown-content">
                                <NavLink to="expenses" onClick={handleLinkClick}>
                                    <i className="fas fa-chart-pie me-2"></i>
                                    <span>Dashboard</span>
                                </NavLink>
                                <NavLink to="expenses/list" onClick={handleLinkClick}>
                                    <i className="fas fa-list me-2"></i>
                                    <span>All Expenses</span>
                                </NavLink>
                                <NavLink to="expenses/new" onClick={handleLinkClick}>
                                    <i className="fas fa-plus me-2"></i>
                                    <span>Add Expense</span>
                                </NavLink>
                                <NavLink to="expenses/reports" onClick={handleLinkClick}>
                                    <i className="fas fa-chart-bar me-2"></i>
                                    <span>Reports</span>
                                </NavLink>

                            </div>
                        )}
                    </div>

                    {/* Packages Dropdown */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.packages ? 'active' : ''}`}
                            onClick={() => toggleDropdown('packages')}
                        >
                            <i className="fas fa-box me-2"></i>
                            <span>Packages</span>
                            <i className={`fas fa-chevron-${openDropdowns.packages ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.packages && (
                            <div className="dropdown-content">
                                <NavLink to="upload-packages" onClick={handleLinkClick}>
                                    <i className="fas fa-upload me-2"></i>
                                    <span>Upload Packages</span>
                                </NavLink>
                                <NavLink to="view-packages" onClick={handleLinkClick}>
                                    <i className="fas fa-eye me-2"></i>
                                    <span>View Packages</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Services Dropdown */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.services ? 'active' : ''}`}
                            onClick={() => toggleDropdown('services')}
                        >
                            <i className="fas fa-cogs me-2"></i>
                            <span>Services</span>
                            <i className={`fas fa-chevron-${openDropdowns.services ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.services && (
                            <div className="dropdown-content">
                                <NavLink to="add-service" onClick={handleLinkClick}>
                                    <i className="fas fa-plus me-2"></i>
                                    <span>Add Service</span>
                                </NavLink>
                                <NavLink to="manage-services" onClick={handleLinkClick}>
                                    <i className="fas fa-edit me-2"></i>
                                    <span>Manage Services</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* FAQs Dropdown */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.faqs ? 'active' : ''}`}
                            onClick={() => toggleDropdown('faqs')}
                        >
                            <i className="fas fa-question-circle me-2"></i>
                            <span>FAQs</span>
                            <i className={`fas fa-chevron-${openDropdowns.faqs ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.faqs && (
                            <div className="dropdown-content">
                                <NavLink to="faqs" onClick={handleLinkClick}>
                                    <i className="fas fa-list me-2"></i>
                                    <span>Manage FAQs</span>
                                </NavLink>
                                <NavLink to="faqs/new" onClick={handleLinkClick}>
                                    <i className="fas fa-plus me-2"></i>
                                    <span>Add FAQ</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Clients Dropdown */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className={`dropdown-btn ${openDropdowns.clients ? 'active' : ''}`}
                            onClick={() => toggleDropdown('clients')}
                        >
                            <i className="fas fa-users me-2"></i>
                            <span>Clients</span>
                            <i className={`fas fa-chevron-${openDropdowns.clients ? 'up' : 'down'} ms-auto`}></i>
                        </button>
                        {openDropdowns.clients && (
                            <div className="dropdown-content">
                                <NavLink to="client-form" onClick={handleLinkClick}>
                                    <i className="fas fa-user-plus me-2"></i>
                                    <span>Add Client</span>
                                </NavLink>
                                <NavLink to="view-clients" onClick={handleLinkClick}>
                                    <i className="fas fa-list me-2"></i>
                                    <span>View Clients</span>
                                </NavLink>
                                <NavLink to="view-clients-analytics" onClick={handleLinkClick}>
                                    <i className="fas fa-chart-bar me-2"></i>
                                    <span>Analysis</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Individual Links */}
                    <NavLink to="blog-manager" onClick={handleLinkClick}>
                        <i className="fas fa-blog me-2"></i>
                        <span>Manage Blogs</span>
                    </NavLink>
                    <NavLink to="gallery" onClick={handleLinkClick} >
                        <i className="fas fa-images me-2"></i>
                        <span>Gallery</span>
                    </NavLink>
                    <NavLink to="upload-receipt" onClick={handleLinkClick} >
                        <i className="fas fa-receipt me-2"></i>
                        <span>Manage Receipts</span>
                    </NavLink>
                    <NavLink to="chatbot-queries" onClick={handleLinkClick}>
                        <i className="fas fa-comments me-2"></i>
                        <span>Chatbot Queries</span>
                    </NavLink>
                    <NavLink to="quotation" onClick={handleLinkClick}>
                        <i className="fas fa-file-pdf me-2"></i>
                        <span>Quotation Generator</span>
                    </NavLink>
                </nav>
            </div>
        </div>
    );
}

export default Sidebar;